// Generic entity accessors — the client half of the entity registry.
//
// Paths, defaults, and writable fields all come from entityRegistry.gen.json
// (generated from backend/shared/entities/registry.js — run
// `node scripts/generate-entities.mjs` after registry edits; never edit the
// JSON by hand). Adding an entity to the registry makes it usable here with
// zero new client code:
//
//   const { docs: workspaces } = useEntityList('workspace', { org: orgId });
//   await createEntity('workspace', { org: orgId }, { name: 'ML' });
//
// Scope objects name ancestor ids by entity: { org, workspace }.
import { createContext, useContext, useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import registry from './entityRegistry.gen.json';
import { API_BASE } from './env';

const ready = () => firebase.apps.length > 0 && db;

export const ENTITIES = registry.entities;
export const ROLES = registry.roles;

// Tenant view: who the current org pages act for. OrgShell provides it
// (org role + the workspaces I belong to + the selected workspace). Field-
// scoped entities (registry `workspaceScoped`: apps, agents, knowledge,
// builder apps) can only be LISTED with a workspaceId constraint unless the
// viewer is an org admin — the rules decide per doc, so useEntityList issues
// one query per visible workspace (+ the org-wide null bucket) and merges.
// Default = unconstrained (admin pages, pre-tenancy callers).
export const TenantContext = createContext({ orgId: null, role: 'owner', workspaceIds: [], workspace: null });
export const useTenant = () => useContext(TenantContext);
const PRIVILEGED = ['admin', 'owner'];
export const isPrivileged = (role) => PRIVILEGED.includes(role);

function parentChain(entityName) {
  const chain = [];
  let cur = ENTITIES[entityName]?.parent;
  while (cur) {
    chain.unshift(cur);
    cur = ENTITIES[cur]?.parent;
  }
  return chain;
}

export function collectionPath(entityName, scope = {}) {
  const def = ENTITIES[entityName];
  if (!def) throw new Error(`unknown entity: ${entityName}`);
  const segs = [];
  for (const anc of parentChain(entityName)) {
    if (!scope[anc]) throw new Error(`missing scope id for '${anc}' (entity ${entityName})`);
    segs.push(ENTITIES[anc].collection, String(scope[anc]));
  }
  segs.push(def.collection);
  return segs.join('/');
}

export const docPath = (entityName, scope, id) => `${collectionPath(entityName, scope)}/${id}`;

// Strip server-only keys and apply declared defaults for a create payload.
function prepareCreate(entityName, payload) {
  const def = ENTITIES[entityName];
  const out = {};
  for (const [key, f] of Object.entries(def.fields)) {
    if (f.serverOnly) continue;
    if (payload[key] !== undefined && payload[key] !== null) out[key] = payload[key];
    else if (f.default !== undefined) out[key] = f.default;
  }
  out.createdAt = out.createdAt || Date.now();
  return out;
}

// ---- Live hooks ---------------------------------------------------------------
// `workspace` option: undefined = follow the tenant view's selection;
// null = all I can see; an id = only that workspace.
export function useEntityList(entityName, scope, { orderBy = 'createdAt', dir = 'desc', enabled = true, workspace, includeOrgWide = false } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});
  const tenant = useTenant();
  const fieldScoped = ENTITIES[entityName]?.scope === 'field';
  const ws = workspace === undefined ? tenant.workspace : workspace;
  // Buckets to query: null = unconstrained; otherwise a list of workspaceId
  // values (null meaning the org-wide bucket).
  let buckets = null;
  if (fieldScoped) {
    if (ws) buckets = includeOrgWide ? [ws, null] : [ws];
    else if (!isPrivileged(tenant.role)) buckets = [null, ...tenant.workspaceIds.slice(0, 20)];
  }
  const bucketsKey = JSON.stringify(buckets);

  useEffect(() => {
    if (!ready() || !enabled) {
      setDocs(null);
      return undefined;
    }
    let path;
    try {
      path = collectionPath(entityName, JSON.parse(scopeKey));
    } catch {
      setDocs(null); // scope incomplete (e.g. still loading an ancestor id)
      return undefined;
    }
    const bks = JSON.parse(bucketsKey);
    if (!bks) {
      let ref = db.collection(path);
      if (orderBy) ref = ref.orderBy(orderBy, dir);
      const unsub = ref.onSnapshot(
        (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => {
          setError(err.message);
          setDocs([]);
        }
      );
      return () => unsub();
    }
    // Segregated: one equality query per bucket (no orderBy → no composite
    // index), merged and sorted client-side. Per-org collections are small.
    const parts = new Map();
    const emit = () => {
      const all = [...parts.values()].flat();
      if (orderBy) all.sort((a, b) => (dir === 'desc' ? 1 : -1) * ((b[orderBy] ?? 0) > (a[orderBy] ?? 0) ? 1 : (b[orderBy] ?? 0) < (a[orderBy] ?? 0) ? -1 : 0));
      setDocs(all);
    };
    const unsubs = bks.map((b, i) => db.collection(path).where('workspaceId', '==', b).onSnapshot(
      (snap) => { parts.set(i, snap.docs.map((d) => ({ id: d.id, ...d.data() }))); emit(); },
      (err) => { setError(err.message); parts.set(i, []); emit(); }
    ));
    return () => unsubs.forEach((u) => u());
  }, [entityName, scopeKey, orderBy, dir, enabled, bucketsKey]);

  return { docs, error };
}

export function useEntityDoc(entityName, scope, id) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});

  useEffect(() => {
    if (!ready() || !id) {
      setDoc(undefined);
      return undefined;
    }
    let path;
    try {
      path = docPath(entityName, JSON.parse(scopeKey), id);
    } catch {
      setDoc(undefined);
      return undefined;
    }
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [entityName, scopeKey, id]);

  return { doc, error };
}

// Human-readable text for a rejected client write. Every form shows this
// inline — a silently swallowed permission error looks like "nothing
// happened" (lesson: silent-client-writes).
export function friendlyError(e) {
  const code = e?.code || '';
  if (code.includes('permission-denied')) {
    return 'Not allowed: your membership in this organization does not permit this write. Ask an org admin, or sign out and back in to refresh your access.';
  }
  if (code.includes('unavailable') || code.includes('network')) {
    return 'Could not reach the database — check your connection and try again.';
  }
  return e?.message || 'Something went wrong — try again.';
}

// ---- CRUD -----------------------------------------------------------------------
// The sidebar's selected workspace (OrgShell keeps this in step with
// TenantContext). Field-scoped creates that do not name a workspaceId land
// there — so a knowledge doc, agent or builder app made while scoped to
// "Research" belongs to Research, not to the whole org.
let currentWorkspace = null;
export function setCurrentWorkspace(id) { currentWorkspace = id || null; }

export async function createEntity(entityName, scope, payload, { id = null } = {}) {
  const col = db.collection(collectionPath(entityName, scope));
  const ref = id ? col.doc(String(id)) : col.doc();
  const data = ENTITIES[entityName]?.scope === 'field' && payload.workspaceId === undefined
    ? { ...payload, workspaceId: currentWorkspace }
    : payload;
  await ref.set(prepareCreate(entityName, data));
  return ref.id;
}

export async function updateEntity(entityName, scope, id, patch) {
  await db.doc(docPath(entityName, scope, id)).set(
    { ...patch, updatedAt: Date.now() },
    { merge: true }
  );
}

export async function deleteEntity(entityName, scope, id) {
  await db.doc(docPath(entityName, scope, id)).delete();
}

// ---- Tenancy-specific helpers ---------------------------------------------------

// Every org I belong to: live collection-group query over my membership docs
// (rules allow reading docs whose uid == mine), joined with the org docs.
export function useMyOrgs() {
  const [orgs, setOrgs] = useState(null);
  const user = firebase.auth().currentUser;
  const uid = user && !user.isAnonymous ? user.uid : null;

  useEffect(() => {
    if (!ready() || !uid) {
      setOrgs(null);
      return undefined;
    }
    const unsub = db
      .collectionGroup('members')
      .where('uid', '==', uid)
      .onSnapshot(
        async (snap) => {
          const memberships = snap.docs
            .map((d) => {
              const segs = d.ref.path.split('/');
              return segs[0] === 'orgs' ? { orgId: segs[1], role: d.data().role } : null;
            })
            .filter(Boolean);
          const loaded = await Promise.all(
            memberships.map(async (m) => {
              try {
                const org = await db.collection('orgs').doc(m.orgId).get();
                return org.exists ? { id: org.id, ...org.data(), myRole: m.role } : null;
              } catch {
                return null;
              }
            })
          );
          const list = loaded.filter(Boolean);
          list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setOrgs(list);
        },
        () => setOrgs([])
      );
    return () => unsub();
  }, [uid]);

  return orgs;
}

// My workspace memberships in one org: [{ workspaceId, role }]. Org
// owner/admin see every workspace by rule; this is what segregates everyone
// else. Live, so a new seat shows up without a reload.
export function useMyWorkspaceRoles(orgId) {
  const [rows, setRows] = useState(null);
  const user = firebase.auth().currentUser;
  const uid = user && !user.isAnonymous ? user.uid : null;
  useEffect(() => {
    if (!ready() || !uid || !orgId) {
      setRows(null);
      return undefined;
    }
    const unsub = db.collectionGroup('wsMembers').where('uid', '==', uid).onSnapshot(
      (snap) => setRows(snap.docs.map((d) => {
        const segs = d.ref.path.split('/'); // orgs/{o}/workspaces/{w}/wsMembers/{uid}
        return segs[0] === 'orgs' && segs[1] === orgId && segs[2] === 'workspaces' ? { workspaceId: segs[3], role: d.data().role } : null;
      }).filter(Boolean)),
      () => setRows([])
    );
    return () => unsub();
  }, [uid, orgId]);
  return rows;
}

// Effective role inside a workspace: org admins/owners carry their org role,
// everyone else their wsMembers row (null = no access).
export function workspaceRole(orgRole, wsRoles, workspaceId) {
  if (isPrivileged(orgRole)) return orgRole;
  return (wsRoles || []).find((r) => r.workspaceId === workspaceId)?.role || null;
}

// Create a workspace + the creator's owner seat in one batch. Org members may
// create workspaces (entitlement-gated); the seat is what lets a non-admin
// creator see inside the workspace they just made.
export async function createWorkspace(orgId, { name, description = '' }) {
  const me = firebase.auth().currentUser;
  if (!me || me.isAnonymous) throw new Error('sign in first');
  const wsRef = db.collection(collectionPath('workspace', { org: orgId })).doc();
  const batch = db.batch();
  batch.set(wsRef, prepareCreate('workspace', { name, description, createdBy: me.uid }));
  batch.set(wsRef.collection('wsMembers').doc(me.uid), prepareCreate('workspaceMember', {
    uid: me.uid, email: me.email || '', displayName: me.displayName || (me.email ? me.email.split('@')[0] : ''), role: 'owner', addedBy: me.uid,
  }));
  await batch.commit();
  return wsRef.id;
}

// Create an org + the creator's owner membership in ONE batch (the generated
// member-bootstrap rule validates this exact shape via getAfter).
// Organizations are born server-side (POST /orgs → shared/signupPolicy.js):
// the email must be verified, a company domain another organization has
// proven refuses with 409 `domain-owned` (err.code, err.org = { id, name,
// joinPolicy }) so the caller can offer "request to join", and the audit
// row is written. The owner membership arrives in the same call; useMyOrgs
// picks it up live.
export async function createOrg({ name, description = '' }) {
  const me = firebase.auth().currentUser;
  if (!me || me.isAnonymous) throw new Error('sign in first');
  const token = await me.getIdToken();
  const res = await fetch(`${API_BASE}/orgs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name, description }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.code || null;
    err.org = data?.org || null;
    throw err;
  }
  return data.orgId;
}

// Public: what the typed email's domain means — { domain, consumer, sso,
// org: { id, name, joinPolicy, allowOtherOrgs } | null }. `org` is set only
// when a company has PROVEN the domain (DNS TXT), never on a bare claim.
export async function discoverSignup(email) {
  const res = await fetch(`${API_BASE}/signup/discover?email=${encodeURIComponent(email)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

// ---- Join requests (registry joinRequest; rules: verified self-create,
// admin decide; claimsWorker seats approved ones) -----------------------------

const joinRequestId = (orgId, uid) => `${orgId}_${uid}`;

export async function createJoinRequest(org, { note = '' } = {}) {
  const me = firebase.auth().currentUser;
  if (!me || me.isAnonymous) throw new Error('sign in first');
  const email = String(me.email || '').toLowerCase();
  await db.collection('joinRequests').doc(joinRequestId(org.id, me.uid)).set(
    prepareCreate('joinRequest', {
      orgId: org.id,
      orgName: org.name || '',
      uid: me.uid,
      email,
      displayName: me.displayName || email.split('@')[0],
      note: String(note || '').slice(0, 500),
    })
  );
  return joinRequestId(org.id, me.uid);
}

// My own requests (any status) — the Organizations page shows "waiting".
export function useMyJoinRequests() {
  const [list, setList] = useState(null);
  const user = firebase.auth().currentUser;
  const uid = user && !user.isAnonymous ? user.uid : null;
  useEffect(() => {
    if (!ready() || !uid) { setList(null); return undefined; }
    const unsub = db.collection('joinRequests').where('uid', '==', uid).onSnapshot(
      (snap) => setList(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error('[joinRequests] self query failed:', err.message); setList([]); }
    );
    return () => unsub();
  }, [uid]);
  return list;
}

// Requests addressed to an org (admin view).
export function useOrgJoinRequests(orgId) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!ready() || !orgId) { setList(null); return undefined; }
    const unsub = db.collection('joinRequests').where('orgId', '==', orgId).onSnapshot(
      (snap) => {
        const l = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        l.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setList(l);
      },
      () => setList([])
    );
    return () => unsub();
  }, [orgId]);
  return list;
}

// Admin decision. approve → the claims worker seats them within seconds;
// decline → they are told by mail. Only status/role/decidedBy/decidedAt may
// change (rules).
export async function decideJoinRequest(requestId, decision, { role = 'member' } = {}) {
  const me = firebase.auth().currentUser;
  const patch = { status: decision === 'approve' ? 'approved' : 'declined', decidedBy: me.uid, decidedAt: Date.now() };
  if (decision === 'approve') patch.role = ['viewer', 'member', 'admin'].includes(role) ? role : 'member';
  await db.collection('joinRequests').doc(requestId).set(patch, { merge: true });
}

export async function withdrawJoinRequest(requestId) {
  await db.collection('joinRequests').doc(requestId).delete();
}

// Create a builder app with its enterprise seed: an assistant policy document
// (draft — approve + publish to make it live) and a baseline test suite, both
// linked to the app (registry.appSeed, from backend policyDefaults.js). The
// app lands first so a seed failure never loses the app; the caller shows
// the seed error inline and the app simply starts without its seed.
export async function createAppWithSeed(orgId, { name, goal = '', createdBy }) {
  const appId = await createEntity('app', { org: orgId }, { name, goal, createdBy });
  const seed = registry.appSeed;
  if (!seed) return { appId, seedError: null };
  const fill = (s) => String(s).replace(/\{\{app\}\}/g, name).replace(/\{\{goal\}\}/g, goal || '(set the goal in App Builder → Definition)');
  try {
    const docId = await createEntity('knowledgeDoc', { org: orgId }, {
      title: fill(seed.policyDoc.title), content: fill(seed.policyDoc.content),
      audience: seed.policyDoc.audience, tags: seed.policyDoc.tags, appIds: [appId], createdBy,
    });
    const suiteId = await createEntity('testSuite', { org: orgId }, {
      name: fill(seed.suite.name), description: seed.suite.description, appId, createdBy,
    });
    await Promise.all(seed.suite.cases.map((c) => createEntity('testCase', { org: orgId, testSuite: suiteId }, c)));
    // `seeded` records what was a default, so App Builder's checklist can
    // say "added for you" instead of showing an earned ✓.
    await updateEntity('app', { org: orgId }, appId, { links: { knowledgeDocIds: [docId], suiteId, seeded: { policyDoc: true, suite: true } } });
    return { appId, seedError: null };
  } catch (e) {
    return { appId, seedError: friendlyError(e) };
  }
}

// Every project in an org, across all workspaces — flattened for the sidebar.
// One workspace subscription + one projects subscription per workspace; org
// project counts are small, so this stays cheap and fully live.
export function useOrgProjects(orgId) {
  return useOrgWorkspaceDocs(orgId, 'projects');
}

// Pipelines across every workspace of the org, same shape — the console
// search lists them as "Workflows".
export function useOrgPipelines(orgId) {
  return useOrgWorkspaceDocs(orgId, 'pipelines');
}

function useOrgWorkspaceDocs(orgId, sub) {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    if (!ready() || !orgId) {
      setProjects(null);
      return undefined;
    }
    const perWs = new Map(); // wsId -> { wsName, docs, unsub }
    const emit = () => {
      const all = [];
      for (const [wsId, e] of perWs) {
        for (const d of e.docs) all.push({ ...d, wsId, wsName: e.wsName });
      }
      all.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setProjects(all);
    };
    const unsubWs = db.collection(`orgs/${orgId}/workspaces`).onSnapshot(
      (snap) => {
        const seen = new Set();
        snap.docs.forEach((wd) => {
          seen.add(wd.id);
          const cur = perWs.get(wd.id);
          if (cur) {
            cur.wsName = wd.data().name || wd.id;
          } else {
            const entry = { wsName: wd.data().name || wd.id, docs: [], unsub: null };
            perWs.set(wd.id, entry);
            entry.unsub = db
              .collection(`orgs/${orgId}/workspaces/${wd.id}/${sub}`)
              .onSnapshot(
                (ps) => {
                  entry.docs = ps.docs.map((d) => ({ id: d.id, ...d.data() }));
                  emit();
                },
                () => {}
              );
          }
        });
        for (const [wsId, e] of perWs) {
          if (!seen.has(wsId)) {
            e.unsub?.();
            perWs.delete(wsId);
          }
        }
        emit();
      },
      () => setProjects([])
    );
    return () => {
      unsubWs();
      perWs.forEach((e) => e.unsub?.());
    };
  }, [orgId, sub]);

  return projects; // [{ id, wsId, wsName, name, ... }] or null while loading
}

// Favorite projects, per org, persisted locally. Keys are `${wsId}/${projectId}`.
export function useFavoriteProjects(orgId) {
  const storageKey = `nv.favProjects.${orgId}`;
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    try {
      setFavs(JSON.parse(localStorage.getItem(storageKey)) || []);
    } catch {
      setFavs([]);
    }
  }, [storageKey]);

  const toggle = (key) => {
    setFavs((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* private mode etc. — favorites just won't persist */
      }
      return next;
    });
  };
  return [favs, toggle];
}

// The caller's role in an org (live).
export function useMyOrgRole(orgId) {
  const user = firebase.auth().currentUser;
  const uid = user && !user.isAnonymous ? user.uid : null;
  const { doc } = useEntityDoc('member', { org: orgId }, orgId && uid ? uid : null);
  return doc === undefined ? undefined : doc?.role || null;
}

export function roleSatisfies(roleHeld, minRole) {
  const have = ROLES.indexOf(roleHeld);
  const need = ROLES.indexOf(minRole);
  return have >= 0 && need >= 0 && have >= need;
}

// ---- Invites ----------------------------------------------------------------------
function randomToken(bytes = 18) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// `workspace` (+ workspaceRole) seats the invitee into one workspace on
// accept — how an org admin authorizes someone from another org for this
// org's projects without giving them more than a viewer's outer ring.
export async function createInvite(org, { email, role = 'member', expiresDays = 14, workspace = null, workspaceRole = 'member' }) {
  const me = firebase.auth().currentUser;
  const token = randomToken();
  await db.collection('invites').doc(token).set(
    prepareCreate('invite', {
      orgId: org.id,
      orgName: org.name || '',
      email: String(email).trim().toLowerCase(),
      role,
      invitedBy: me.uid,
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      ...(workspace ? { workspaceId: workspace.id, workspaceName: workspace.name || '', workspaceRole } : {}),
    })
  );
  return token;
}

// Invites addressed to me (matched on my signed-in email).
export function useMyInvites() {
  const [invites, setInvites] = useState(null);
  const user = firebase.auth().currentUser;
  const email = user && !user.isAnonymous ? (user.email || '').toLowerCase() : null;

  useEffect(() => {
    if (!ready() || !email) {
      setInvites(null);
      return undefined;
    }
    const unsub = db
      .collection('invites')
      .where('email', '==', email)
      .where('status', '==', 'pending')
      .onSnapshot(
        (snap) => setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => {
          // Never silent: a denied query here once hid every pending invite
          // and invitees created their own orgs instead.
          console.error('[invites] pending-invite query failed:', err.message);
          setInvites([]);
        }
      );
    return () => unsub();
  }, [email]);

  return invites;
}

// Invites an org has outstanding (admin view).
export function useOrgInvites(orgId) {
  const [invites, setInvites] = useState(null);
  useEffect(() => {
    if (!ready() || !orgId) {
      setInvites(null);
      return undefined;
    }
    const unsub = db
      .collection('invites')
      .where('orgId', '==', orgId)
      .onSnapshot(
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setInvites(list);
        },
        () => setInvites([])
      );
    return () => unsub();
  }, [orgId]);
  return invites;
}

// Accept: flip the invite; the backend claims worker creates the membership
// (admin SDK) and marks the invite done — the UI reacts via useMyOrgs.
export async function acceptInvite(inviteId) {
  const me = firebase.auth().currentUser;
  await db.collection('invites').doc(inviteId).set(
    { status: 'accepted', acceptedByUid: me.uid },
    { merge: true }
  );
}

export async function revokeInvite(inviteId) {
  await db.collection('invites').doc(inviteId).set({ status: 'revoked' }, { merge: true });
}
